import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Clock, Sparkles, MapPin, Unlock } from "lucide-react";
import { trackSchedule } from "../lib/analytics";
import { useSEO } from "../lib/useSEO";

// GoHighLevel / LeadConnector calendar widget id (from booking embed code)
const GHL_CALENDAR_ID = "L05V9KxZKktuNMPpAolk";
const GHL_IFRAME_ID = "Q49SCdXzLr8zB27n4pZb_1782433029712";
const GHL_BASE = `https://api.leadconnectorhq.com/widget/booking/${GHL_CALENDAR_ID}`;

const HIGHLIGHTS = [
  { Icon: Sparkles, text: "Plug & Play Engine" },
  { Icon: MapPin, text: "Local Territory Leads" },
  { Icon: Unlock, text: "No lock-in contracts" },
];

const BookingPage = () => {
  const [lead, setLead] = useState(null);

  useSEO({
    title: "Book a Call with Chris — The 7 Figure Cleaner",
    description:
      "Book a 15-minute strategy call to lock in your exclusive territory. One exterior-cleaning business per suburb across Australia.",
    path: "/booking",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const raw = localStorage.getItem("7fc_latest_application");
      if (raw) setLead(JSON.parse(raw));
    } catch (_) {}
    // Meta Pixel: user landed on the booking page (calendar shown)
    trackSchedule({ content_name: "Strategy Call Booking" });
  }, []);

  // Inject the GHL form embed script once (handles widget resizing / postMessage)
  useEffect(() => {
    const src = "https://link.msgsndr.com/js/form_embed.js";
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const calendarUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (lead?.firstName) params.set("first_name", lead.firstName);
    if (lead?.lastName) params.set("last_name", lead.lastName);
    if (lead?.email) params.set("email", lead.email);
    if (lead?.phone) params.set("phone", lead.phone);
    const qs = params.toString();
    return qs ? `${GHL_BASE}?${qs}` : GHL_BASE;
  }, [lead]);

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip bg-white text-brand-black">
      {/* Minimal top bar — logo only, no menu, no CTA */}
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" className="flex items-center" aria-label="Back to home">
            <img
              src="/logo/logo-lg.webp"
              alt="The 7 Figure Cleaner logo"
              className="h-12 w-auto object-contain sm:h-[58px]"
              width="800"
              height="288"
            />
          </Link>
          <div className="hidden items-center gap-2 text-xs font-black uppercase tracking-wider text-brand-grayMid sm:flex">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Secure Booking
          </div>
        </div>
      </header>
      <main className="flex-1">
        {/* Top banner */}
        <section className="bg-brand-blue">
          <div className="mx-auto max-w-5xl px-5 py-12 text-center sm:px-8 sm:py-16">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-white ring-1 ring-white/20">
              <ShieldCheck className="h-3.5 w-3.5" /> Next Step
            </p>
            <h1 className="text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Schedule Your <span className="text-blue-100">30 Min Call</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-blue-50 sm:text-lg">
              On this call we&apos;ll share how the 7 Figure Cleaner engine works and how it could plug into your existing business.
            </p>
            <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-bold uppercase tracking-wide text-blue-50 sm:text-sm">
              {HIGHLIGHTS.map(({ Icon, text }) => (
                <li key={text} className="inline-flex items-center gap-1.5">
                  <Icon className="h-4 w-4" /> {text}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Calendar embed */}
        <section className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
          <Link
            to="/offer"
            className="mb-5 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-grayMid hover:text-brand-blue"
          >
            <ArrowLeft className="h-4 w-4" /> Back to territory check
          </Link>

          <div className="rounded-3xl bg-white p-3 shadow-sm ring-1 ring-gray-200 sm:p-5">
            <div className="overflow-hidden rounded-2xl">
              <iframe
                src={calendarUrl}
                title="Book a 30 minute call with Chris"
                id={GHL_IFRAME_ID}
                style={{ width: "100%", border: "none", overflow: "hidden", minHeight: 820 }}
                scrolling="no"
              />
            </div>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-brand-grayMid">
              <Clock className="h-3.5 w-3.5" /> Takes about 30 minutes &middot; Zero obligation
            </p>
          </div>
        </section>
      </main>
      {/* Minimal footer — distraction-free */}
      <footer className="border-t border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-5 py-5 text-xs text-brand-grayMid sm:flex-row sm:px-8">
          <p>© {new Date().getFullYear()} The 7 Figure Cleaner.</p>
          <p className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Your booking is private &amp; secure.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;
